import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import api from "../../services/api";
import EmptyState from "../../components/admin/EmptyState";
import LoadingSkeleton from "../../components/common/LoadingSkeleton";
import { getApiErrorMessage } from "./market/marketUtils";

const ACTION_OPTIONS = [
  { value: "ALL", label: "전체 액션" },
  { value: "DREAM_REQUEST_DELETE", label: "요청 삭제" },
  { value: "DREAM_REQUEST_VOID", label: "완료 요청 무효" },
  { value: "DREAM_REQUEST_VOID_RESTORE", label: "무효 처리 해제" },
  { value: "DREAM_USER_HARD_DELETE", label: "계정 영구 삭제" },
  { value: "DREAM_USER_STATUS_CHANGE", label: "계정 상태 변경" },
];

const TARGET_OPTIONS = [
  { value: "ALL", label: "전체 대상" },
  { value: "collection_market_location", label: "수거 요청" },
  { value: "users", label: "이용자/작업자" },
];

const ROLE_OPTIONS = [
  { value: "ALL", label: "전체 처리자" },
  { value: "admin", label: "관리자" },
  { value: "partner", label: "파트너" },
];

const actionLabels = ACTION_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

const targetLabels = TARGET_OPTIONS.reduce((acc, option) => {
  acc[option.value] = option.label;
  return acc;
}, {});

function todayString() {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, "0");
  const dd = String(now.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function thirtyDaysAgoString() {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function formatDateTime(value) {
  if (!value) return "-";
  return new Date(value).toLocaleString("ko-KR");
}

function metadataSummary(metadata) {
  if (!metadata) return "-";
  return metadata
    .split(";")
    .filter(Boolean)
    .slice(0, 4)
    .join(" · ");
}

function requestSearchUrl(targetId) {
  const search = new URLSearchParams({
    q: String(targetId),
    fromDate: "",
    toDate: "",
  });
  return `/admin/dream/requests?${search.toString()}`;
}

function AdminActionLogPage() {
  const [filters, setFilters] = useState({
    actionType: "ALL",
    targetType: "ALL",
    actorRole: "ALL",
    fromDate: thirtyDaysAgoString(),
    toDate: todayString(),
    keyword: "",
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [logs, setLogs] = useState([]);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const params = useMemo(() => ({
    ...filters,
    keyword: filters.keyword.trim() || undefined,
    page,
    size: rowsPerPage,
  }), [filters, page, rowsPerPage]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await api.get("/api/admin/action-logs", { params });
      setLogs(res.data?.content || []);
      setTotalElements(res.data?.totalElements || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, "처리 이력을 불러오지 못했습니다."));
      setLogs([]);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(0);
  };

  const resetFilters = () => {
    setFilters({
      actionType: "ALL",
      targetType: "ALL",
      actorRole: "ALL",
      fromDate: thirtyDaysAgoString(),
      toDate: todayString(),
      keyword: "",
    });
    setPage(0);
  };

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>처리 이력</Typography>
          <Typography color="text.secondary" sx={{ mt: 0.5 }}>
            삭제, 상태 변경처럼 되돌리기 어려운 운영 액션의 처리자, 처리일, 사유를 확인합니다.
          </Typography>
        </Box>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchLogs} disabled={loading}>
          새로고침
        </Button>
      </Box>

      <Alert severity="info" sx={{ mb: 2 }}>
        이 화면은 관리자/파트너 전용 감사 이력입니다. 앱 공용 API와 분리된 경로에서 조회되며, 기존 앱 사용자 화면에는 노출되지 않습니다.
      </Alert>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>액션</InputLabel>
              <Select
                label="액션"
                value={filters.actionType}
                onChange={(e) => updateFilter("actionType", e.target.value)}
              >
                {ACTION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>대상</InputLabel>
              <Select
                label="대상"
                value={filters.targetType}
                onChange={(e) => updateFilter("targetType", e.target.value)}
              >
                {TARGET_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>처리자</InputLabel>
              <Select
                label="처리자"
                value={filters.actorRole}
                onChange={(e) => updateFilter("actorRole", e.target.value)}
              >
                {ROLE_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              size="small"
              label="시작일"
              type="date"
              value={filters.fromDate}
              onChange={(e) => updateFilter("fromDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <TextField
              size="small"
              label="종료일"
              type="date"
              value={filters.toDate}
              onChange={(e) => updateFilter("toDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button variant="text" fullWidth sx={{ height: 40 }} onClick={resetFilters}>
              필터 초기화
            </Button>
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              size="small"
              label="검색"
              placeholder="대상 ID, 사유, 처리자 이름/이메일, 메타데이터 검색"
              value={filters.keyword}
              onChange={(e) => updateFilter("keyword", e.target.value)}
              InputProps={{ startAdornment: <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} /> }}
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? (
        <LoadingSkeleton />
      ) : logs.length === 0 ? (
        <EmptyState message="조건에 맞는 처리 이력이 없습니다." />
      ) : (
        <Paper>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>처리일</TableCell>
                  <TableCell>액션</TableCell>
                  <TableCell>대상</TableCell>
                  <TableCell>처리자</TableCell>
                  <TableCell>사유</TableCell>
                  <TableCell>메타데이터</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {logs.map((log) => (
                  <TableRow key={log.logId} hover>
                    <TableCell sx={{ minWidth: 150 }}>{formatDateTime(log.createdAt)}</TableCell>
                    <TableCell>
                      <Chip size="small" color="primary" variant="outlined" label={actionLabels[log.actionType] || log.actionType} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{targetLabels[log.targetType] || log.targetType}</Typography>
                      {log.targetType === "collection_market_location" && log.targetId ? (
                        <Typography
                          component={Link}
                          to={requestSearchUrl(log.targetId)}
                          variant="caption"
                          sx={{ color: "primary.main", textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
                        >
                          #{log.targetId}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="text.secondary">#{log.targetId}</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.25}>
                        <Typography variant="body2">{log.actorName || "시스템"}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.actorRole || "-"} {log.actorEmail ? `· ${log.actorEmail}` : ""}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260 }}>
                      <Typography variant="body2" sx={{ whiteSpace: "pre-wrap" }}>
                        {log.reason || "-"}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={log.metadata || ""}>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {metadataSummary(log.metadata)}
                        </Typography>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalElements}
            page={page}
            onPageChange={(_, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 30, 50, 100]}
            labelRowsPerPage="페이지당 행"
          />
        </Paper>
      )}
    </Box>
  );
}

export default AdminActionLogPage;
